"use client"

import { postSearchGetPriceRangeByBrands } from "@/api/generated/BMWApi"
import {
  Brand,
  CarModel,
  CarModelSpecialStrip,
  HomePage as HomePageData,
  SearchPageContent,
  SpecialStripDetails,
  ViewDataModel,
} from "@/api/generated/BMWInterfaces"
import { expandBrandIds } from "@/lib/common/brandRelations"
import { Desktop, Mobile } from "@/lib/common/MediaQueries"
import { modelsPageView } from "@/lib/gtm"
import styled from "@emotion/styled"
import { useRouter, useSearchParams } from "next/navigation"
import { Suspense, useEffect, useMemo, useRef, useState } from "react"
import { isModelActive } from "../Homepage/PremiumDeals"
import BenefitHero from "./BenefitHero"
import CustomBreadcrumbs from "../shared/CustomBreadcrumbs"
import { FilterDesktop } from "./FilterDesktop"
import { SearchContent } from "./SearchContent"
import { useHasSearchHero } from "./SearchHeroLayout"
import { SwipeableEdgeDrawer } from "./Sheet"
import StickyButton from "./StickyButton"

const StyledLayout = styled.div<{ $hasHero: boolean }>`
  padding: 40px 16px 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0px 40px 56px 40px;
    gap: 48px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 40px 40px 80px;
    gap: 48px;
  }
  ${(props) => props.theme.breakpoints.down("md")} {
    padding: 0px 16px 80px;
    gap: 48px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    ${({ $hasHero }) => $hasHero && "padding-top: 48px;"}
  }
`

const InnerLayout = styled.div`
  display: flex;

  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 40px;
  }
`

export type VehicleType = {
  value: string
  label: string
}

type Props = {
  modelsData: CarModel[]
  homeData: HomePageData
  brands: Brand[]
  engineTypes?: ViewDataModel[]
  preSelectedBrand?: Brand
  showBenefitTimer?: boolean
  benefitName?: string
  benefitId?: string
  benefitHeroDetails?: SpecialStripDetails
  searchPageContent?: SearchPageContent | null
}
function Search({
  homeData,
  modelsData,
  brands,
  engineTypes,
  preSelectedBrand,
  showBenefitTimer = false,
  benefitName,
  benefitId,
  benefitHeroDetails,
  searchPageContent,
}: Props): React.ReactElement {
  const cars = modelsData.filter((el) => {
    return isModelActive(el)
  })

  const activeBenefits = modelsData
    .filter((model) => {
      return model.specialStrip?.duoDate && new Date(model.specialStrip.duoDate) >= new Date()
    })
    ?.map((model) => model.specialStrip)
    .filter(Boolean)
  const uniqueListOfBenefits: CarModelSpecialStrip[] = Array.from(
    new Set(activeBenefits?.map((benefit) => benefit?.id)),
  )
    ?.map((id) => activeBenefits.find((benefit) => benefit?.id === id))
    .filter(Boolean) as CarModelSpecialStrip[]

  const vehicleTypes = [
    { value: "new", label: "רכב חדש" },
    { value: "old", label: "רכב 0 ק״מ" },
  ]
  const [pathname, setPathname] = useState<string>("")
  const router = useRouter()
  const searchParams = useSearchParams()
  const minPrice = searchParams.get("minPrice")
  const maxPrice = searchParams.get("maxPrice")
  const isBrandPage = !!preSelectedBrand
  const brandId = isBrandPage
    ? [preSelectedBrand.id.toString()]
    : searchParams.getAll("brandId").flatMap((id) => id.split(","))
  const engineTypeId = searchParams.getAll("engineType").flatMap((id) => id.split(","))
  const [minMaxPrice, setMinMaxPrice] = useState<number[]>([Number(minPrice), Number(maxPrice)])
  const [brandMin, setBrandMin] = useState<number>(0)
  const [brandMax, setBrandMax] = useState<number>(1)
  const [openFilter, setOpenFilter] = useState<boolean>(false)
  const [chosenBrand, setChosenBrand] = useState<string[]>(brandId || [])
  const [chosenType, setChosenType] = useState<string[]>([])
  const [chosenEngineType, setChosenEngineType] = useState<string[]>(engineTypeId || [])
  const [filterHighToLow, setFilterHighToLow] = useState<"high" | "low">("low")
  const [brandChoose, setBrandChoose] = useState<string>(preSelectedBrand?.name || "")
  const [results, setResults] = useState<CarModel[]>(cars)
  const [filtersCleared, setFiltersCleared] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const initialMountRef = useRef(true)
  const selectedBrandMeta = useMemo(() => {
    if (chosenBrand.length === 1) {
      return brands.find((b) => b.id.toString() === chosenBrand[0])
    }
    return undefined
  }, [chosenBrand, brands])
  // A single brand shows its own catalog copy, falling back to the search page content when the
  // brand's fields are empty; every other filter combination shows the search page content.
  const brandText = selectedBrandMeta?.catalogText
  const headerTitle = selectedBrandMeta?.catalogTitle || searchPageContent?.title
  const headerIntro = brandText ? null : searchPageContent?.intro
  const updateResults = async ({
    brandParam,
    vehicleTypeParam,
    engineTypeParam,
  }: {
    brandParam?: string[]
    vehicleTypeParam?: string[]
    engineTypeParam?: string[]
  }): Promise<void> => {
    // Clear any pending update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current)
    }
    // Debounce updates to prevent multiple rapid calls
    await new Promise((resolve) => {
      updateTimeoutRef.current = setTimeout(resolve, 150)
    })

    let resultsToUpdate = modelsData
    if (minMaxPrice[0] !== 0 || minMaxPrice[1] !== 0) {
      resultsToUpdate = modelsData.filter((model) => {
        const price = model.discountPrice || model.price
        return price >= minMaxPrice[0] && price <= minMaxPrice[1]
      })
    }
    const latestBrands = brandParam || chosenBrand
    if (latestBrands.length > 0) {
      const expandedBrands = expandBrandIds(latestBrands, brands)
      resultsToUpdate = resultsToUpdate.filter((model) => {
        return expandedBrands.includes(model.brand.id.toString())
      })
    }

    const latestEngineTypes = engineTypeParam || chosenEngineType
    if (latestEngineTypes.length > 0) {
      resultsToUpdate = resultsToUpdate.filter((model) => {
        return latestEngineTypes.includes(model.enginType?.toString())
      })
    }

    const selectedIdsNum = latestBrands.map(Number)
    const selectedNames = brands
      .filter((item) => selectedIdsNum.includes(item.id))
      .map((item) => item.name)
      .join(", ")
    setBrandChoose(selectedNames)

    const typeParam = vehicleTypeParam || chosenType
    if (typeParam?.length === 1) {
      setResults(
        resultsToUpdate
          .filter((model) =>
            typeParam?.[0] === vehicleTypes?.[0].value ? model.isNewCar : !model.isNewCar,
          )
          .filter((model) => new Date(model.dueDate) > new Date()),
      )
    } else {
      setResults(resultsToUpdate.filter((model) => new Date(model.dueDate) > new Date()))
    }

    setIsLoading(false)
  }

  const updateUrl = (): void => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(searchParams.toString())

      params.delete("minPrice")
      params.delete("maxPrice")

      const minVal = minMaxPrice[0]
      const maxVal = minMaxPrice[1]

      if (minVal && minVal > 0) {
        params.set("minPrice", minVal.toString())
      }

      if (maxVal && maxVal > 0 && maxVal !== minVal) {
        params.set("maxPrice", maxVal.toString())
      }

      if (chosenBrand.length > 0) {
        params.set("brandId", chosenBrand.join(","))
      } else {
        params.delete("brandId")
      }

      if (chosenType?.length > 0) {
        params.set("vehicleType", chosenType.join(","))
      } else {
        params.delete("vehicleType")
      }

      if (chosenEngineType?.length > 0) {
        params.set("engineType", chosenEngineType.join(","))
      } else {
        params.delete("engineType")
      }

      router.replace(`${pathname}?${params.toString()}`)
    }
  }

  const getAllRange = async (forceUpdate: boolean = false): Promise<void> => {
    try {
      const selectedIds = brands.map((brand) => brand.id)
      const res = await postSearchGetPriceRangeByBrands(selectedIds)

      if (res && res.data && res.data.body) {
        const minPriceValue = res.data.body.minPrice || 1
        const maxPriceValue = res.data.body.maxPrice || 1000000

        setBrandMin(minPriceValue)
        setBrandMax(maxPriceValue)

        if (!minPrice || !maxPrice || forceUpdate) {
          setMinMaxPrice([minPriceValue, maxPriceValue])
        }
      }
    } catch (error) {
      console.error("Failed to get price range:", error)
      setBrandMin(1)
      setBrandMax(1000000)
      if (!minPrice || !maxPrice) {
        setMinMaxPrice([1, 1000000])
      }
    }
  }

  const getNewValuesFilter = (): void => {
    if (minMaxPrice[0] === 0 && minMaxPrice[1] === 0) {
      getAllRange()
      setResults(cars)
      updateUrl()
    } else {
      updateResults({})
      updateUrl()
    }
  }

  const handleChange = (
    event: Event,
    newValue: number | number[],
    autoUpdate: boolean = false,
  ): void => {
    setFiltersCleared(false)
    const values = newValue as number[]

    if (Array.isArray(values) && values.length === 2) {
      const validMin = values[0] || brandMin || 1
      const validMax = values[1] || brandMax || 1000000

      setMinMaxPrice([validMin, validMax])

      if (autoUpdate) {
        setIsLoading(true)
        setTimeout(() => {
          const params = new URLSearchParams(searchParams.toString())
          params.delete("minPrice")
          params.delete("maxPrice")
          if (validMin && validMin > 0) {
            params.set("minPrice", validMin.toString())
          }
          if (validMax && validMax > 0 && validMax !== validMin) {
            params.set("maxPrice", validMax.toString())
          }
          router.replace(`${pathname}?${params.toString()}`)
          updateResults({})
        }, 300)
      }
    }
  }

  const handleChangeChosenBrand =
    (brand: Brand, autoUpdate: boolean = false) =>
    (): void => {
      setFiltersCleared(false)
      let newChosenBrand: string[]
      if (chosenBrand.includes(brand.id.toString())) {
        newChosenBrand = chosenBrand.filter((item) => item !== brand.id.toString())
      } else {
        newChosenBrand = [...chosenBrand, brand.id.toString()]
      }
      setChosenBrand(newChosenBrand)

      if (autoUpdate) {
        setIsLoading(true)
        setTimeout(() => {
          const params = new URLSearchParams(searchParams.toString())
          if (newChosenBrand.length > 0) {
            params.set("brandId", newChosenBrand.join(","))
          } else {
            params.delete("brandId")
          }
          router.replace(`${pathname}?${params.toString()}`)
          updateResults({ brandParam: newChosenBrand })
        }, 0)
      }
    }
  const handleChangeChosenType =
    (type: VehicleType, autoUpdate: boolean = false) =>
    (): void => {
      setFiltersCleared(false)
      let newChosenType: string[]
      if (chosenType.includes(type.value.toString())) {
        newChosenType = chosenType.filter((item) => item !== type.value.toString())
      } else {
        newChosenType = [...chosenType, type.value.toString()]
      }
      setChosenType(newChosenType)

      if (autoUpdate) {
        setIsLoading(true)
        setTimeout(() => {
          const params = new URLSearchParams(searchParams.toString())
          if (newChosenType.length > 0) {
            params.set("vehicleType", newChosenType.join(","))
          } else {
            params.delete("vehicleType")
          }
          router.replace(`${pathname}?${params.toString()}`)
          updateResults({ vehicleTypeParam: newChosenType })
        }, 0)
      }
    }
  const handleChangeChosenEngineType =
    (engineType: { id: number; name: string }, autoUpdate: boolean = false) =>
    (): void => {
      setFiltersCleared(false)
      let newChosenEngineType: string[]
      if (chosenEngineType.includes(engineType.id.toString())) {
        newChosenEngineType = chosenEngineType.filter((item) => item !== engineType.id.toString())
      } else {
        newChosenEngineType = [...chosenEngineType, engineType.id.toString()]
      }
      setChosenEngineType(newChosenEngineType)

      if (autoUpdate) {
        setIsLoading(true)
        setTimeout(() => {
          const params = new URLSearchParams(searchParams.toString())
          if (newChosenEngineType.length > 0) {
            params.set("engineType", newChosenEngineType.join(","))
          } else {
            params.delete("engineType")
          }
          router.replace(`${pathname}?${params.toString()}`)
          updateResults({ engineTypeParam: newChosenEngineType })
        }, 0)
      }
    }
  const activeFiltersCount = useMemo(() => {
    let count = 0
    if (chosenBrand.length > 0) count += expandBrandIds(chosenBrand, brands).length
    if (chosenType.length > 0) count += chosenType.length
    if (chosenEngineType.length > 0) count += chosenEngineType.length
    if (
      (minMaxPrice[0] > brandMin || minMaxPrice[1] < brandMax) &&
      minMaxPrice[0] !== 0 &&
      minMaxPrice[1] !== 0
    )
      count += 1
    return count
  }, [chosenBrand, chosenType, chosenEngineType, minMaxPrice, brandMin, brandMax, brands])

  const chosenBrandsWithNames = useMemo(() => {
    const expandedBrands = expandBrandIds(chosenBrand, brands)
    return expandedBrands
      .map((id) => {
        const brand = brands.find((b) => b.id.toString() === id)
        return { id, name: brand?.name || "" }
      })
      .filter((b) => b.name)
  }, [chosenBrand, brands])

  const chosenEngineTypesWithNames = useMemo(() => {
    return chosenEngineType
      .map((id) => {
        const engineType = engineTypes?.find((et) => et.id.toString() === id)
        return { id, name: engineType?.name || "" }
      })
      .filter((et) => et.name)
  }, [chosenEngineType, engineTypes])

  const chosenVehicleTypesWithLabels = useMemo(() => {
    return chosenType
      .map((value) => {
        const type = vehicleTypes.find((vt) => vt.value === value)
        return type || { value, label: "" }
      })
      .filter((vt) => vt.label)
  }, [chosenType, vehicleTypes])

  const hasPriceFilter = useMemo(() => {
    return (
      (minMaxPrice[0] > brandMin || minMaxPrice[1] < brandMax) &&
      minMaxPrice[0] !== 0 &&
      minMaxPrice[1] !== 0
    )
  }, [minMaxPrice, brandMin, brandMax])

  const priceFilter = useMemo(() => {
    return results
      .filter((el) => {
        return isModelActive(el)
      })
      .sort((a, b) => {
        const currentPriceA = a.discountPrice || a.price
        const currentPriceB = b.discountPrice || b.price
        if (filterHighToLow === "high") {
          return currentPriceB - currentPriceA
        }
        return currentPriceA - currentPriceB
      })
  }, [filterHighToLow, results])
  const updateBrandsPrice = async (): Promise<void> => {
    let selectedIds
    if (chosenBrand.length === 0) {
      selectedIds = brands.map((brand) => brand.id)
    } else {
      const expandedBrands = expandBrandIds(chosenBrand, brands)
      selectedIds = expandedBrands.map(Number)
    }
    const res = await postSearchGetPriceRangeByBrands(selectedIds)
    if (res) {
      const hasUrlPriceParams = minPrice && maxPrice

      if (hasUrlPriceParams) {
        setBrandMin(res.data.body.minPrice)
        setBrandMax(res.data.body.maxPrice)
      } else {
        if (minMaxPrice[1] >= res.data.body.maxPrice && minMaxPrice[0] <= res.data.body.minPrice) {
          setMinMaxPrice([res.data.body.minPrice, res.data.body.maxPrice])
        } else if (
          minMaxPrice[1] >= res.data.body.maxPrice &&
          minMaxPrice[0] >= res.data.body.minPrice &&
          minMaxPrice[0] <= res.data.body.maxPrice
        ) {
          setMinMaxPrice([minMaxPrice[0], res.data.body.maxPrice])
        } else if (
          minMaxPrice[1] >= res.data.body.maxPrice &&
          minMaxPrice[0] >= res.data.body.minPrice &&
          minMaxPrice[0] >= res.data.body.maxPrice
        ) {
          setMinMaxPrice([res.data.body.minPrice, res.data.body.maxPrice])
        } else if (
          minMaxPrice[1] <= res.data.body.minPrice &&
          minMaxPrice[0] <= res.data.body.minPrice
        ) {
          setMinMaxPrice([res.data.body.minPrice, minMaxPrice[1]])
        } else if (
          minMaxPrice[0] >= res.data.body.minPrice &&
          minMaxPrice[0] <= res.data.body.maxPrice &&
          minMaxPrice[1] >= res.data.body.minPrice &&
          minMaxPrice[1] <= res.data.body.maxPrice
        ) {
          setMinMaxPrice([res.data.body.minPrice, res.data.body.maxPrice])
        } else {
          setMinMaxPrice([minMaxPrice[0], minMaxPrice[1]])
        }

        setBrandMin(res.data.body.minPrice)
        setBrandMax(res.data.body.maxPrice)
      }
    }
  }
  const clearAllFilters = async (): Promise<void> => {
    setResults(cars)
    setBrandChoose("")
    setFiltersCleared(true)
    setChosenBrand([])
    setChosenType([])
    setChosenEngineType([])
    router.replace(pathname)
    await getAllRange(true)
  }

  const handleRemoveBrand = (brandId: string): void => {
    const newChosenBrand = chosenBrand.filter((id) => id !== brandId)
    setChosenBrand(newChosenBrand)
    const params = new URLSearchParams(searchParams.toString())
    if (newChosenBrand.length > 0) {
      params.set("brandId", newChosenBrand.join(","))
    } else {
      params.delete("brandId")
    }
    router.replace(`${pathname}?${params.toString()}`)
    updateResults({ brandParam: newChosenBrand })
  }

  const handleRemoveEngineType = (engineTypeId: string): void => {
    const newChosenEngineType = chosenEngineType.filter((id) => id !== engineTypeId)
    setChosenEngineType(newChosenEngineType)
    const params = new URLSearchParams(searchParams.toString())
    if (newChosenEngineType.length > 0) {
      params.set("engineType", newChosenEngineType.join(","))
    } else {
      params.delete("engineType")
    }
    router.replace(`${pathname}?${params.toString()}`)
    updateResults({ engineTypeParam: newChosenEngineType })
  }

  const handleRemoveVehicleType = (typeValue: string): void => {
    const newChosenType = chosenType.filter((type) => type !== typeValue)
    setChosenType(newChosenType)
    const params = new URLSearchParams(searchParams.toString())
    if (newChosenType.length > 0) {
      params.set("vehicleType", newChosenType.join(","))
    } else {
      params.delete("vehicleType")
    }
    router.replace(`${pathname}?${params.toString()}`)
    updateResults({ vehicleTypeParam: newChosenType })
  }

  const handleRemovePriceFilter = (): void => {
    setMinMaxPrice([brandMin, brandMax])
    const params = new URLSearchParams(searchParams.toString())
    params.delete("minPrice")
    params.delete("maxPrice")
    router.replace(`${pathname}?${params.toString()}`)
    updateResults({})
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (isBrandPage) {
        setPathname("/search")
      } else {
        setPathname(window.location.pathname)
      }
    }

    if (minPrice && maxPrice) {
      getAllRange()

      const urlMinPrice = Number(minPrice)
      const urlMaxPrice = Number(maxPrice)
      setMinMaxPrice([urlMinPrice, urlMaxPrice])

      updateResults({})
    } else {
      getAllRange()

      if (brandId && brandId.length > 0) {
        updateResults({})
      } else {
        setResults(cars)
      }
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (filtersCleared) {
      return
    }
    if (minPrice && maxPrice) {
      updateBrandsPrice()
    }
  }, [chosenBrand, filtersCleared])

  useEffect(() => {
    // Skip on initial mount - let the first useEffect handle it
    if (initialMountRef.current) {
      initialMountRef.current = false
      return
    }

    let brandId = searchParams.getAll("brandId")
    let vehicleType = searchParams.getAll("vehicleType")
    let engineType = searchParams.getAll("engineType")
    const minPrice = searchParams.get("minPrice")
    const maxPrice = searchParams.get("maxPrice")
    brandId = brandId.flatMap((id) => id.split(","))
    vehicleType = vehicleType.flatMap((type) => type.split(","))
    engineType = engineType.flatMap((type) => type.split(","))

    if (
      brandId.length === 0 &&
      !minPrice &&
      !maxPrice &&
      pathname === "/search" &&
      vehicleType.length === 0 &&
      engineType.length === 0
    ) {
      clearAllFilters()
    } else if (vehicleType.length > 0 || brandId.length > 0 || engineType.length > 0) {
      const brandIdParam = brandId.length > 0 ? brandId : undefined
      if (brandIdParam) {
        setChosenBrand(brandId)
      }

      const vehicleTypeParam = vehicleType.length > 0 ? vehicleType : undefined
      if (vehicleTypeParam) {
        setChosenType(vehicleType)
      }

      const engineTypeParam = engineType.length > 0 ? engineType : undefined
      if (engineTypeParam) {
        setChosenEngineType(engineType)
      }

      updateResults({
        brandParam: brandIdParam,
        vehicleTypeParam: vehicleTypeParam,
        engineTypeParam: engineTypeParam,
      })
    }
  }, [searchParams])

  useEffect(() => {
    modelsPageView()
  }, [])

  // The search layout renders the hero and, above it, the desktop breadcrumbs.
  const hasHero = useHasSearchHero()

  return (
    <>
      {!!benefitHeroDetails && <BenefitHero details={benefitHeroDetails} />}
      <StyledLayout $hasHero={hasHero}>
        {!hasHero && (
          <Desktop>
            <CustomBreadcrumbs
              current={{
                title: benefitName || "לכל ההצעות",
                url: benefitId ? `/benefit/${benefitId}` : "/search",
              }}
              parents={[{ title: "ראשי", url: "/" }]}
            />
          </Desktop>
        )}
        <InnerLayout>
          <Desktop>
            <FilterDesktop
              brandMax={brandMax}
              brandMin={brandMin}
              chosenBrand={chosenBrand}
              chosenType={chosenType}
              chosenEngineType={chosenEngineType}
              clearAllFilters={clearAllFilters}
              getNewValuesFilter={getNewValuesFilter}
              handleChange={handleChange}
              handleChangeChosenBrand={handleChangeChosenBrand}
              handleChangeChosenType={handleChangeChosenType}
              handleChangeChosenEngineType={handleChangeChosenEngineType}
              brands={brands}
              types={vehicleTypes}
              engineTypes={engineTypes || []}
              minMaxPrice={minMaxPrice}
            />
          </Desktop>
          <SearchContent
            minMaxPrice={minMaxPrice}
            brandChoose={brandChoose}
            description={brandText}
            title={headerTitle}
            intro={headerIntro}
            openMobileFilters={() => {
              setOpenFilter(!openFilter)
              if (!isBrandPage) {
                getNewValuesFilter()
              }
            }}
            filterHighToLow={filterHighToLow}
            homeData={homeData}
            results={priceFilter}
            setFilterHighToLow={setFilterHighToLow}
            isLoading={isLoading}
            activeFiltersCount={activeFiltersCount}
            chosenBrands={chosenBrandsWithNames}
            chosenEngineTypes={chosenEngineTypesWithNames}
            chosenVehicleTypes={chosenVehicleTypesWithLabels}
            onRemoveBrand={handleRemoveBrand}
            onRemoveEngineType={handleRemoveEngineType}
            onRemoveVehicleType={handleRemoveVehicleType}
            onRemovePriceFilter={handleRemovePriceFilter}
            hasPriceFilter={hasPriceFilter}
            activeBenefits={uniqueListOfBenefits}
            showBenefitTimer={showBenefitTimer}
            isBenefitPage={!!benefitId}
          />
          <Mobile>
            <SwipeableEdgeDrawer
              minMaxPrice={minMaxPrice}
              setMinMaxPrice={setMinMaxPrice}
              setBrandChoose={setBrandChoose}
              setOpenFilter={setOpenFilter}
              openFilter={openFilter}
              getNewValuesFilter={getNewValuesFilter}
              setFiltersCleared={setFiltersCleared}
              clearAllFilters={clearAllFilters}
              brandMax={brandMax}
              brandMin={brandMin}
              chosenBrand={chosenBrand}
              chosenType={chosenType}
              brands={brands}
              types={vehicleTypes}
              handleChosenBrand={handleChangeChosenBrand}
              handleChangeChosenType={handleChangeChosenType}
              engineTypes={engineTypes || []}
              chosenEngineType={chosenEngineType}
              handleChangeChosenEngineType={handleChangeChosenEngineType}
            />
          </Mobile>
        </InnerLayout>
        <StickyButton />
      </StyledLayout>
    </>
  )
}

export default function SearchWithSuspense(props: Props): React.ReactElement {
  return (
    <Suspense>
      <Search {...props} />
    </Suspense>
  )
}
